import React, { useState, useEffect, useCallback } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { motion, useTransform, useScroll, AnimatePresence } from 'framer-motion';
import { Calculator, Shield, Clock, TrendingUp, Users, Award, CheckCircle, ArrowRight, Star } from 'lucide-react';
import './TripleTrustHero.css';

const TripleTrustHero = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [fundingAmount, setFundingAmount] = useState('');
  const [industry, setIndustry] = useState('');
  const [showEstimate, setShowEstimate] = useState(false);
  const [availableSlots, setAvailableSlots] = useState(15);
  const [currentStat, setCurrentStat] = useState(0);

  // Animated counters
  const [fundingCounter, setFundingCounter] = useState(0);
  const [hoursCounter, setHoursCounter] = useState(0);
  const [successCounter, setSuccessCounter] = useState(0);

  const handleResize = useCallback(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  useEffect(() => {
    let timeoutId;
    const throttledResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', throttledResize, { passive: true });
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', throttledResize);
    };
  }, [handleResize]);

  // Parallax effect
  const { scrollY } = useScroll();
  const backgroundY = useTransform(scrollY, [0, 500], [0, isMobile ? 50 : 100]);

  // Counter animations
  useEffect(() => {
    const fundingInterval = setInterval(() => {
      setFundingCounter(prev => prev < 150 ? prev + 3 : 150);
    }, 50);

    const hoursInterval = setInterval(() => {
      setHoursCounter(prev => prev < 72 ? prev + 2 : 72);
    }, 80);

    const successInterval = setInterval(() => {
      setSuccessCounter(prev => prev < 95 ? prev + 2 : 95);
    }, 60);

    setTimeout(() => {
      clearInterval(fundingInterval);
      clearInterval(hoursInterval);
      clearInterval(successInterval);
    }, 3000);

    return () => {
      clearInterval(fundingInterval);
      clearInterval(hoursInterval);
      clearInterval(successInterval);
    };
  }, []);

  // Rotating stats
  const stats = [
    {
      icon: TrendingUp,
      value: `$${fundingCounter}B+`,
      label: "Funding Sourced",
      color: "stat-green"
    },
    {
      icon: Clock,
      value: `${hoursCounter}hr`,
      label: "Avg Approval Time",
      color: "stat-blue"
    },
    {
      icon: CheckCircle,
      value: `${successCounter}%`,
      label: "Success Rate",
      color: "stat-orange"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStat(prev => (prev + 1) % stats.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [stats.length]);

  // Client logos (mock data)
  const clientLogos = ['Microsoft', 'Apple', 'Google', 'Amazon', 'Tesla', 'Samsung'];

  // Industries for dropdown
  const industries = [
    'Technology', 'Real Estate', 'Manufacturing', 'Healthcare',
    'Energy', 'Infrastructure', 'Retail', 'Agriculture', 'Other'
  ];

  const handleCalculate = () => {
    if (fundingAmount && industry) {
      setShowEstimate(true);
      // Simulate slot reduction
      setAvailableSlots(prev => Math.max(prev - 1, 0));
    }
  };

  const getEstimatedRate = () => {
    switch(industry) {
      case 'Real Estate': return '3.2%';
      case 'Technology': return '4.1%';
      case 'Infrastructure': return '2.8%';
      default: return '3.8%';
    }
  };

  const getCurrentStatIcon = () => {
    const IconComponent = stats[currentStat].icon;
    return <IconComponent className={`stat-icon ${stats[currentStat].color}`} />;
  };

  // Scroll offset for mobile optimization
  const scrollOffset = isMobile ? -50 : -70;

  return (
      
      <section id="hero" className="triple-trust-hero" role="banner">
        {/* Animated Background */}
        <motion.div
          className="hero-background"
          style={{ y: backgroundY }}
          role="img"
          aria-label="Trinity Financing hero background"
        />

        <div className="hero-container">

          {/* Trust Bar - Client Logos */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="trust-bar"
          >
            <div className="trust-header">
              <Users className="trust-icon" />
              <span className="trust-text">Trusted by 500+ Global Companies</span>
            </div>
            <div className="client-logos">
              {clientLogos.map((logo, index) => (
                <motion.div
                  key={logo}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="client-logo"
                >
                  {logo}
                </motion.div>
              ))}
            </div>
          </motion.div>

          <div className="hero-grid">

            {/* Left Column - Main Content */}
            <div className="hero-left">

              {/* Rotating Power Stats */}
              <motion.div
                className="power-stats"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStat}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.5 }}
                    className="stat-display"
                  >
                    {getCurrentStatIcon()}
                    <div className="stat-content">
                      <div className={`stat-value ${stats[currentStat].color}`}>
                        {stats[currentStat].value}
                      </div>
                      <div className="stat-label">
                        {stats[currentStat].label}
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </motion.div>

              {/* Main Headlines */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <h1 className="hero-main-title">
                  Get <span className="highlight-orange">$1M-$500B</span><br />
                   Project Financing With <span className="highlight-green">Zero Upfront Fees</span>
                </h1>
              </motion.div>

              <motion.p
                className="hero-description"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Access exclusive funding sources others can't reach. We've sourced over $150 billion
                for companies worldwide with our proven 72-hour approval process.
              </motion.p>

              {/* Guarantee Badges */}
              <motion.div
                className="guarantee-badges"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <div className="guarantee-badge">
                  <Shield className="badge-icon green" />
                  <span className="badge-text">No Review Fee, No Upfront Fees</span>
                </div>
                <div className="guarantee-badge">
                  <Award className="badge-icon orange" />
                  <span className="badge-text">A+ Rating Since 2018</span>
                </div>
              </motion.div>

              {/* Urgency Element */}
              <motion.div
                className="urgency-bar"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <div className="urgency-content">
                  <div className="urgency-left">
                    <div className="pulse-indicator"></div>
                    <h2 className="urgency-text">Project Financing & Trade Finance</h2>
                  </div>
                  <div className="urgency-count">
                    {availableSlots} left this week
                  </div>
                </div>
              </motion.div>

            </div>

            {/* Right Column - Funding Calculator */}
            {!isMobile && (
            <motion.div
              className="calculator-card"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="calculator-header">
                <Calculator className="calculator-icon" />
                <h3 className="calculator-title">
                  Trinity-Financing-Funding-Options
                </h3>
                <p className="calculator-subtitle">Get an instant estimate in 60 seconds</p>
              </div>

              <div className="calculator-form">
                <div className="form-group">
                  <h4 className="form-label">
                    Funding Amount Needed
                  </h4>
                  <div className="amount-input">
                    <span className="currency-symbol">$</span>
                    <input
                      type="number"
                      value={fundingAmount}
                      onChange={(e) => setFundingAmount(e.target.value)}
                      placeholder="1,000,000"
                      className="amount-field"
                    />
                    <span className="currency-suffix">USD</span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Industry
                  </label>
                  <select
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="industry-select"
                  >
                    <option value="">Select your industry</option>
                    {industries.map(ind => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={handleCalculate}
                  disabled={!fundingAmount || !industry}
                  className="calculate-button"
                >
                  <Calculator className="button-icon" />
                  Get Instant Estimate - FREE
                </button>

                {/* Estimate Results */}
                <AnimatePresence>
                  {showEstimate && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="estimate-results"
                    >
                      <div className="results-header">
                        <CheckCircle className="results-icon" />
                        <span className="results-title">Pre-Qualified!</span>
                      </div>
                      <div className="results-details">
                        <div className="result-row">
                          <span>Estimated Rate:</span>
                          <span className="result-value green">{getEstimatedRate()} APR</span>
                        </div>
                        <div className="result-row">
                          <span>Processing Time:</span>
                          <span className="result-value">72 hours</span>
                        </div>
                        <div className="result-row">
                          <span>Success Probability:</span>
                          <span className="result-value green">High (95%)</span>
                        </div>
                      </div>
                      <ScrollLink
                        to="contact"
                        smooth={true}
                        duration={800}
                        offset={scrollOffset}
                        className="consultation-button"
                      >
                        Book Free Consultation
                        <ArrowRight className="button-icon" />
                      </ScrollLink>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Trust Elements */}
                <div className="trust-footer">
                  <div className="rating-stars">
                    {[1,2,3,4,5].map(star => (
                      <Star key={star} className="star-icon" />
                    ))}
                  </div>
                  <p className="rating-text">
                    4.9/5 from 2,500+ satisfied clients
                  </p>
                </div>
              </div>
            </motion.div>
            )}

          </div>

          {/* Bottom CTA */}
          <motion.div
            className="bottom-cta"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <ScrollLink
              to="contact"
              smooth={true}
              duration={800}
              offset={scrollOffset}
              className="main-cta-button"
            >
              <h3 className="main-cta-heading">Apply Now for Financing</h3>
            </ScrollLink>
            <p className="cta-disclaimer">No obligations • Free consultation • 24/7 support</p>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="scroll-indicator"
          animate={{
            y: [0, isMobile ? 8 : 12, 0],
          }}
          transition={{
            repeat: Infinity,
            duration: isMobile ? 1.2 : 1.5,
            ease: "easeInOut"
          }}
        >
          <ScrollLink
            to="services"
            smooth={true}
            duration={800}
            offset={scrollOffset}
            className="scroll-link"
            aria-label="Scroll to next section"
          >
            <div className="scroll-icon" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <span className="scroll-text">Scroll Down</span>
          </ScrollLink>
        </motion.div>
      </section>
  
  );
};

export default TripleTrustHero;
